import { generateSHA256Hash } from './crypto';
import { gzipCompress } from './gzip';
import { createLogger, LOG_NAMESPACES } from './logger';

// Keep max chunk size small enough so the gzip-compressed result stays under 1 MB.
// At a typical ~37 % compression ratio, 2 MB of raw JSON ≈ 740 KB compressed,
// which comfortably fits within the backend's upload-size limit.
const MAX_CHUNK_SIZE_BYTES = 2 * 1024 * 1024;
const logger = createLogger(LOG_NAMESPACES.DATA);

export interface ChunkResult {
  hash: string;
  compressedData: Uint8Array;
  originalSize: number;
  compressedSize: number;
}

export interface JsonChunkingResult {
  chunks: ChunkResult[];
  totalChunks: number;
  totalOriginalSize: number;
  totalCompressedSize: number;
}

export async function chunkJsonData(
  jsonData: unknown[],
  maxChunkSize: number = MAX_CHUNK_SIZE_BYTES,
  onProgress?: (progress: number) => void,
): Promise<JsonChunkingResult> {
  logger.info(`Starting JSON chunking: ${jsonData.length} items, max chunk ${maxChunkSize} bytes`);
  const chunks: ChunkResult[] = [];
  let currentBatch: unknown[] = [];
  let currentBatchSize = 0;
  const total = jsonData.length;

  for (let idx = 0; idx < jsonData.length; idx++) {
    const item = jsonData[idx];
    const itemJson = JSON.stringify(item);
    const itemSize = new TextEncoder().encode(itemJson).length;

    if (currentBatchSize + itemSize > maxChunkSize && currentBatch.length > 0) {
      logger.debug(`Creating chunk from batch of ${currentBatch.length} items`);
      const chunk = await processBatch(currentBatch);
      chunks.push(chunk);
      currentBatch = [];
      currentBatchSize = 0;
    }

    currentBatch.push(item);
    currentBatchSize += itemSize;

    // Report progress: 0–100 relative to items processed
    if (onProgress && total > 0) {
      onProgress(Math.round(((idx + 1) / total) * 100));
    }
  }

  if (currentBatch.length > 0) {
    logger.debug(`Creating final chunk from batch of ${currentBatch.length} items`);
    const chunk = await processBatch(currentBatch);
    chunks.push(chunk);
  }

  const totalOriginalSize = chunks.reduce((sum, c) => sum + c.originalSize, 0);
  const totalCompressedSize = chunks.reduce((sum, c) => sum + c.compressedSize, 0);

  // Log hash for each chunk
  for (let i = 0; i < chunks.length; i++) {
    logger.info(`Chunk ${i + 1}/${chunks.length} — hash: ${chunks[i].hash}, compressed: ${chunks[i].compressedSize} bytes`);
  }

  logger.info(`Chunking complete: ${chunks.length} chunks, total compressed ${totalCompressedSize} bytes`);

  return {
    chunks,
    totalChunks: chunks.length,
    totalOriginalSize,
    totalCompressedSize,
  };
}

async function processBatch(batch: unknown[]): Promise<ChunkResult> {
  logger.trace(`Processing batch: ${batch.length} items`);
  const jsonString = JSON.stringify(batch);
  logger.trace(`Batch stringified: ${jsonString.length} characters`);
  let originalSize = new TextEncoder().encode(jsonString).length;
  logger.trace(`Original size: ${originalSize} bytes`);
  let compressedData = gzipCompress(jsonString);
  let compressedSize = compressedData.length;
  logger.trace(`Initial compression: ${compressedSize} bytes`);

  // If compressed size exceeds max, reduce batch and retry (simple backoff)
  while (compressedSize > MAX_CHUNK_SIZE_BYTES && batch.length > 1) {
    logger.warn(`Compressed batch too large (${compressedSize} > ${MAX_CHUNK_SIZE_BYTES}), reducing size`);
    batch.pop(); // Remove last item
    const reducedString = JSON.stringify(batch);
    originalSize = new TextEncoder().encode(reducedString).length;
    compressedData = gzipCompress(reducedString);
    compressedSize = compressedData.length;
    logger.trace(`Reduced to ${batch.length} items, compressed ${compressedSize} bytes`);
  }

  const hash = await generateSHA256Hash(compressedData);
  logger.trace(`Hash generated: ${hash}`);

  return {
    hash,
    compressedData,
    originalSize,
    compressedSize: compressedData.length,
  };
}
